import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useAuth } from "../contexts/AuthContext";
import ErrorBanner from "../components/ErrorBanner";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { getUserDisplayName } from "../shared/utils/userDisplay";
import { fetchTasksForAccount } from "../shared/api/tasks";
import { serverInfo } from "../shared/config/serverConfig";
import {
  fetchSavedInsights,
  createSavedInsight,
  deleteSavedInsight,
} from "../shared/api/savedInsights";
import { getDueDatePart, getDueInstantForComparison } from "../shared/utils/dueDateTime";


import { apiFetch } from "../shared/api/apiFetch";

const TASK_CHART_COLORS = [
  "#2563eb",
  "#ea580c",
  "#16a34a",
  "#9333ea",
  "#dc2626",
  "#0891b2",
  "#ca8a04",
  "#db2777",
  "#65a30d",
  "#4f46e5",
];

const CHART_MAX_WIDTH = 820;
const CHART_MAX_HEIGHT = 320;

function TaskStepLineChart({ data, seriesKeys, colors }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({
    width: CHART_MAX_WIDTH,
    height: CHART_MAX_HEIGHT,
  });
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setSize({
        width: Math.min(CHART_MAX_WIDTH, Math.max(200, width)),
        height: Math.min(CHART_MAX_HEIGHT, Math.max(180, height)),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data.length || !seriesKeys.length) return null;

  const padding = { top: 24, right: 24, bottom: 36, left: 52 };
  const chartW = size.width - padding.left - padding.right;
  const chartH = size.height - padding.top - padding.bottom;
  const months = data.map((d) => d.month);
  const allValues = data.flatMap((d) => seriesKeys.map((k) => d[k] || 0));
  const yMax = Math.max(1, Math.ceil(Math.max(...allValues) * 1.05));
  const xScale = (i) => padding.left + (i / Math.max(1, months.length - 1)) * chartW;
  const yScale = (v) => padding.top + chartH - (v / yMax) * chartH;

  const stepPath = (key) => {
    const pts = [];
    data.forEach((d, i) => {
      const v = d[key] != null ? d[key] : 0;
      const x = xScale(months.length === 1 ? 0 : i);
      const y = yScale(v);
      if (i === 0) pts.push(`M ${x} ${y}`);
      else {
        pts.push(
          `L ${x} ${yScale(
            data[i - 1][key] != null ? data[i - 1][key] : 0
          )}`
        );
        pts.push(`L ${x} ${y}`);
      }
    });
    return pts.join(" ");
  };

  const tickCount = 6;
  const yTicks = [];
  for (let i = 0; i <= tickCount; i++) {
    const v = (yMax * i) / tickCount;
    yTicks.push({ v, y: yScale(v) });
  }
  const xTickStep = Math.max(1, Math.floor(months.length / 8));
  const xTicks = months.filter(
    (_, i) => i % xTickStep === 0 || i === months.length - 1
  );

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: CHART_MAX_WIDTH,
        height: CHART_MAX_HEIGHT + 44,
        minHeight: 220,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          border: "1px solid #e9ecef",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: "#fafbfc",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        <svg
          width={size.width}
          height={size.height}
          style={{ overflow: "visible", display: "block" }}
        >
          <defs>
            <pattern
              id="task-insight-grid"
              width={40}
              height={40}
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="#e9ecef"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect
            x={padding.left}
            y={padding.top}
            width={chartW}
            height={chartH}
            fill="url(#task-insight-grid)"
          />
          <text
            x={padding.left + chartW / 2}
            y={size.height - 6}
            textAnchor="middle"
            fontSize={11}
            fill="#495057"
          >
            Month
          </text>
          <text
            x={14}
            y={padding.top + chartH / 2}
            textAnchor="middle"
            fontSize={11}
            fill="#495057"
            transform={`rotate(-90, 14, ${padding.top + chartH / 2})`}
          >
            Amount ($)
          </text>
          {yTicks.map(({ v, y }) => (
            <g key={v}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + chartW}
                y2={y}
                stroke="#dee2e6"
                strokeWidth={1}
              />
              <text
                x={padding.left - 6}
                y={y + 4}
                textAnchor="end"
                fontSize={10}
                fill="#6c757d"
              >
                {v === 0 ? "0" : `$${Math.round(v)}`}
              </text>
            </g>
          ))}
          {xTicks.map((m) => {
            const i = months.indexOf(m);
            if (i < 0) return null;
            const x = xScale(months.length === 1 ? 0 : i);
            return (
              <g key={m}>
                <text
                  x={x}
                  y={padding.top + chartH + 18}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#6c757d"
                >
                  {m}
                </text>
              </g>
            );
          })}
          {seriesKeys.map((key, i) => (
            <path
              key={key}
              d={stepPath(key)}
              fill="none"
              stroke={colors[i % colors.length]}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
          {seriesKeys.map((key, i) =>
            data.map((d, j) => {
              const x = xScale(months.length === 1 ? 0 : j);
              const v = d[key] != null ? d[key] : 0;
              const y = yScale(v);
              const isHover =
                hover && hover.month === d.month && hover.key === key;
              return (
                <circle
                  key={`${key}-${j}`}
                  cx={x}
                  cy={y}
                  r={isHover ? 5 : 3}
                  fill={colors[i % colors.length]}
                  stroke="#fff"
                  strokeWidth={1}
                  onMouseEnter={() =>
                    setHover({ month: d.month, key, value: v })
                  }
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: "pointer" }}
                />
              );
            })
          )}
        </svg>
        {hover && (
          <div
            style={{
              position: "absolute",
              left: padding.left + 20,
              top: padding.top + 16,
              background: "rgba(255,255,255,0.98)",
              padding: "6px 10px",
              borderRadius: 6,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              fontSize: 12,
              pointerEvents: "none",
            }}
          >
            <strong>{hover.month}</strong> · {hover.key}:{" "}
            {`$${Number(hover.value).toLocaleString()}`}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px 20px",
            marginTop: 8,
            fontSize: 12,
          }}
        >
          {seriesKeys.map((key, i) => (
            <span
              key={key}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <span
                style={{
                  width: 12,
                  height: 3,
                  backgroundColor: colors[i % colors.length],
                  borderRadius: 1,
                }}
              />
              {key}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function TaskLineChart({ data, seriesKeys, colors }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({
    width: CHART_MAX_WIDTH,
    height: CHART_MAX_HEIGHT,
  });
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setSize({
        width: Math.min(CHART_MAX_WIDTH, Math.max(200, width)),
        height: Math.min(CHART_MAX_HEIGHT, Math.max(180, height)),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data.length || !seriesKeys.length) return null;

  const padding = { top: 24, right: 24, bottom: 36, left: 52 };
  const chartW = size.width - padding.left - padding.right;
  const chartH = size.height - padding.top - padding.bottom;
  const months = data.map((d) => d.month);
  const allValues = data.flatMap((d) => seriesKeys.map((k) => d[k] || 0));
  const yMax = Math.max(1, Math.ceil(Math.max(...allValues) * 1.05));
  const xScale = (i) => padding.left + (i / Math.max(1, months.length - 1)) * chartW;
  const yScale = (v) => padding.top + chartH - (v / yMax) * chartH;

  const linePath = (key) => {
    const pts = data.map((d, i) => {
      const v = d[key] != null ? d[key] : 0;
      const x = xScale(months.length === 1 ? 0 : i);
      const y = yScale(v);
      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    });
    return pts.join(" ");
  };

  const tickCount = 6;
  const yTicks = [];
  for (let i = 0; i <= tickCount; i++) {
    const v = (yMax * i) / tickCount;
    yTicks.push({ v, y: yScale(v) });
  }
  const xTickStep = Math.max(1, Math.floor(months.length / 8));
  const xTicks = months.filter(
    (_, i) => i % xTickStep === 0 || i === months.length - 1
  );

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: CHART_MAX_WIDTH,
        height: CHART_MAX_HEIGHT + 44,
        minHeight: 220,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          border: "1px solid #e9ecef",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: "#fafbfc",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        <svg
          width={size.width}
          height={size.height}
          style={{ overflow: "visible", display: "block" }}
        >
          <defs>
            <pattern
              id="task-insight-grid-line"
              width={40}
              height={40}
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="#e9ecef"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect
            x={padding.left}
            y={padding.top}
            width={chartW}
            height={chartH}
            fill="url(#task-insight-grid-line)"
          />
          <text
            x={padding.left + chartW / 2}
            y={size.height - 6}
            textAnchor="middle"
            fontSize={11}
            fill="#495057"
          >
            Month
          </text>
          <text
            x={14}
            y={padding.top + chartH / 2}
            textAnchor="middle"
            fontSize={11}
            fill="#495057"
            transform={`rotate(-90, 14, ${padding.top + chartH / 2})`}
          >
            Amount ($)
          </text>
          {yTicks.map(({ v, y }) => (
            <g key={v}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + chartW}
                y2={y}
                stroke="#dee2e6"
                strokeWidth={1}
              />
              <text
                x={padding.left - 6}
                y={y + 4}
                textAnchor="end"
                fontSize={10}
                fill="#6c757d"
              >
                {v === 0 ? "0" : `$${Math.round(v)}`}
              </text>
            </g>
          ))}
          {xTicks.map((m) => {
            const i = months.indexOf(m);
            if (i < 0) return null;
            const x = xScale(months.length === 1 ? 0 : i);
            return (
              <g key={m}>
                <text
                  x={x}
                  y={padding.top + chartH + 18}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#6c757d"
                >
                  {m}
                </text>
              </g>
            );
          })}
          {seriesKeys.map((key, i) => (
            <path
              key={key}
              d={linePath(key)}
              fill="none"
              stroke={colors[i % colors.length]}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
          {seriesKeys.map((key, i) =>
            data.map((d, j) => {
              const x = xScale(months.length === 1 ? 0 : j);
              const v = d[key] != null ? d[key] : 0;
              const y = yScale(v);
              const isHover =
                hover && hover.month === d.month && hover.key === key;
              return (
                <circle
                  key={`${key}-${j}`}
                  cx={x}
                  cy={y}
                  r={isHover ? 5 : 3}
                  fill={colors[i % colors.length]}
                  stroke="#fff"
                  strokeWidth={1}
                  onMouseEnter={() =>
                    setHover({ month: d.month, key, value: v })
                  }
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: "pointer" }}
                />
              );
            })
          )}
        </svg>
        {hover && (
          <div
            style={{
              position: "absolute",
              left: padding.left + 20,
              top: padding.top + 16,
              background: "rgba(255,255,255,0.98)",
              padding: "6px 10px",
              borderRadius: 6,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              fontSize: 12,
              pointerEvents: "none",
            }}
          >
            <strong>{hover.month}</strong> · {hover.key}:{" "}
            {`$${Number(hover.value).toLocaleString()}`}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px 20px",
            marginTop: 8,
            fontSize: 12,
          }}
        >
          {seriesKeys.map((key, i) => (
            <span
              key={key}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <span
                style={{
                  width: 12,
                  height: 3,
                  backgroundColor: colors[i % colors.length],
                  borderRadius: 1,
                }}
              />
              {key}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function TaskBarChart({ data, seriesKeys, colors }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({
    width: CHART_MAX_WIDTH,
    height: CHART_MAX_HEIGHT,
  });
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setSize({
        width: Math.min(CHART_MAX_WIDTH, Math.max(200, width)),
        height: Math.min(CHART_MAX_HEIGHT, Math.max(180, height)),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data.length || !seriesKeys.length) return null;

  const padding = { top: 24, right: 24, bottom: 36, left: 52 };
  const chartW = size.width - padding.left - padding.right;
  const chartH = size.height - padding.top - padding.bottom;
  const months = data.map((d) => d.month);
  const allValues = data.flatMap((d) => seriesKeys.map((k) => d[k] || 0));
  const yMax = Math.max(1, Math.ceil(Math.max(...allValues) * 1.05));
  const yScale = (v) => padding.top + chartH - (v / yMax) * chartH;
  const nMonths = months.length;
  const nSeries = seriesKeys.length;
  const groupWidth = chartW / Math.max(1, nMonths);
  const barGap = 2;
  const barWidth = Math.max(
    2,
    (groupWidth - barGap * (nSeries - 1)) / nSeries
  );
  const xCenter = (i) => padding.left + (i + 0.5) * groupWidth;
  const tickCount = 6;
  const yTicks = [];
  for (let i = 0; i <= tickCount; i++) {
    const v = (yMax * i) / tickCount;
    yTicks.push({ v, y: yScale(v) });
  }
  const xTickStep = Math.max(1, Math.floor(months.length / 8));
  const xTicks = months.filter(
    (_, i) => i % xTickStep === 0 || i === months.length - 1
  );

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: CHART_MAX_WIDTH,
        height: CHART_MAX_HEIGHT + 44,
        minHeight: 220,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          border: "1px solid #e9ecef",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: "#fafbfc",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        <svg
          width={size.width}
          height={size.height}
          style={{ overflow: "visible", display: "block" }}
        >
          <defs>
            <pattern
              id="task-insight-grid-bar"
              width={40}
              height={40}
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="#e9ecef"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect
            x={padding.left}
            y={padding.top}
            width={chartW}
            height={chartH}
            fill="url(#task-insight-grid-bar)"
          />
          <text
            x={padding.left + chartW / 2}
            y={size.height - 6}
            textAnchor="middle"
            fontSize={11}
            fill="#495057"
          >
            Month
          </text>
          <text
            x={14}
            y={padding.top + chartH / 2}
            textAnchor="middle"
            fontSize={11}
            fill="#495057"
            transform={`rotate(-90, 14, ${padding.top + chartH / 2})`}
          >
            Amount ($)
          </text>
          {yTicks.map(({ v, y }) => (
            <g key={v}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + chartW}
                y2={y}
                stroke="#dee2e6"
                strokeWidth={1}
              />
              <text
                x={padding.left - 6}
                y={y + 4}
                textAnchor="end"
                fontSize={10}
                fill="#6c757d"
              >
                {v === 0 ? "0" : `$${Math.round(v)}`}
              </text>
            </g>
          ))}
          {xTicks.map((m) => {
            const i = months.indexOf(m);
            if (i < 0) return null;
            const x = xCenter(i);
            return (
              <g key={m}>
                <text
                  x={x}
                  y={padding.top + chartH + 18}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#6c757d"
                >
                  {m}
                </text>
              </g>
            );
          })}
          {seriesKeys.map((key, seriesIdx) =>
            data.map((d, j) => {
              const v = d[key] != null ? d[key] : 0;
              if (v <= 0) return null;
              const baseX = padding.left + j * groupWidth;
              const offset = seriesIdx * (barWidth + barGap);
              const x = baseX + offset;
              const barH = (v / yMax) * chartH;
              const y = yScale(v);
              const isHover =
                hover && hover.month === d.month && hover.key === key;
              return (
                <rect
                  key={`${key}-${j}`}
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barH}
                  fill={colors[seriesIdx % colors.length]}
                  stroke={isHover ? "#1e3a8a" : "#fff"}
                  strokeWidth={isHover ? 2 : 1}
                  rx={2}
                  onMouseEnter={() =>
                    setHover({ month: d.month, key, value: v })
                  }
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: "pointer" }}
                />
              );
            })
          )}
        </svg>
        {hover && (
          <div
            style={{
              position: "absolute",
              left: padding.left + 20,
              top: padding.top + 16,
              background: "rgba(255,255,255,0.98)",
              padding: "6px 10px",
              borderRadius: 6,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              fontSize: 12,
              pointerEvents: "none",
            }}
          >
            <strong>{hover.month}</strong> · {hover.key}:{" "}
            {`$${Number(hover.value).toLocaleString()}`}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px 20px",
            marginTop: 8,
            fontSize: 12,
          }}
        >
          {seriesKeys.map((key, i) => (
            <span
              key={key}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: colors[i % colors.length],
                  borderRadius: 2,
                }}
              />
              {key}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function getMonthPart(dueDate) {
  const ymd = getDueDatePart(dueDate);
  return ymd ? ymd.slice(0, 7) : "";
}

function buildTaskInsightChartData(filteredList, viewBy) {
  if (!filteredList.length) return { data: [], seriesKeys: [] };
  const monthSums = {};
  filteredList.forEach((task) => {
    const month = getMonthPart(task.due_date);
    if (!month) return;
    const amt = parseFloat(task.amount) || 0;
    const groupKey =
      viewBy === "project"
        ? (task.project || "").trim()
        : viewBy === "task"
          ? (task.name || "").trim()
          : (task.category || "General").trim();
    if (!groupKey && viewBy !== "category") return;
    const key = groupKey || "(blank)";
    if (!monthSums[month]) monthSums[month] = {};
    monthSums[month][key] = (monthSums[month][key] || 0) + amt;
  });
  const months = Object.keys(monthSums).sort();
  const seriesKeys = [
    ...new Set(
      filteredList.map((t) => {
        const g =
          viewBy === "project"
            ? (t.project || "").trim()
            : viewBy === "task"
              ? (t.name || "").trim()
              : (t.category || "General").trim();
        return g || "(blank)";
      })
    ),
  ]
    .filter(Boolean)
    .sort();
  const data = months.map((month) => {
    const row = { month };
    seriesKeys.forEach((k) => {
      row[k] = monthSums[month][k] || 0;
    });
    return row;
  });
  return { data, seriesKeys };
}

function FinancialInsights() {
  const { userInfo, loading: authLoading, authError } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const account_id = userInfo?.account_id;
  const user_id = userInfo?._id;

  const [CurrentObjs, setCurrentObjs] = useState([]);
  const [ProjectObjs, setProjectObjs] = useState([]);
  const [UserObjs, setUserObjs] = useState([]);

  const [, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters (copied from Task view so insights behave the same)
  const [searchTerm, setSearchTerm] = useState("");

  const [categoryFilter, setCategoryFilter] = useState(new Set());
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);

  const [projectFilter, setProjectFilter] = useState(new Set());
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);

  const [timePhaseFilter, setTimePhaseFilter] = useState("");

  const [insightViewBy, setInsightViewBy] = useState("project");
  // Single view type: 'table' | 'bar' | 'line' | 'stepline'
  const [viewType, setViewType] = useState("line");

  // Saved insights (name + current filters/view settings)
  const [savedInsights, setSavedInsights] = useState([]);
  const [saveAsName, setSaveAsName] = useState("");
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [savedInsightsDropdownOpen, setSavedInsightsDropdownOpen] = useState(false);

  const filtersRowRef = useRef(null);
  const savedInsightsDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const projectDropdownRef = useRef(null);

  const categoryButtonRef = useRef(null);
  const projectButtonRef = useRef(null);

  const [categoryMenuPos, setCategoryMenuPos] = useState(null);
  const [projectMenuPos, setProjectMenuPos] = useState(null);

  // Fetch tasks
  useEffect(() => {
    if (authLoading) return;
    if (!account_id) {
      setCurrentObjs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    fetchTasksForAccount(account_id, { user_id: userInfo?._id || "", role: userInfo?.role || "" })
      .then((data) => {
        setCurrentObjs(Array.isArray(data) ? data : []);
        setError("");
        setLoading(false);
      })
      .catch((err) => {
        setError(
          getMessageForFetchError(
            err,
            "Unable to load financial insights. Please try again."
          )
        );
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when account_id or authLoading changes
  }, [account_id, authLoading]);

  // Fetch users (for search by owner name)
  useEffect(() => {
    if (!account_id) {
      setUserObjs([]);
      return;
    }
    const url = serverInfo.serverUrl + "/getUser";
    apiFetch(url, {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setUserObjs(Array.isArray(data) ? data : []))
      .catch(() => setUserObjs([]));
  }, [account_id]);

  // Fetch projects for Project filter
  useEffect(() => {
    if (!account_id) {
      setProjectObjs([]);
      return;
    }
    const url = serverInfo.serverUrl + "/getProject";
    apiFetch(url, {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setProjectObjs(Array.isArray(data) ? data : []))
      .catch(() => setProjectObjs([]));
  }, [account_id]);

  // Keep dropdown menus visible even inside horizontal scroll container
  useEffect(() => {
    const scroller = filtersRowRef.current;

    const getMenuPos = (btnRef, fallbackWidth) => {
      const el = btnRef.current;
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return {
        top: Math.round(r.bottom + 2),
        left: Math.round(r.left),
        width: Math.max(fallbackWidth || 0, Math.round(r.width)),
      };
    };

    function updatePositions() {
      setCategoryMenuPos(categoryDropdownOpen ? getMenuPos(categoryButtonRef, 160) : null);
      setProjectMenuPos(projectDropdownOpen ? getMenuPos(projectButtonRef, 180) : null);
    }

    updatePositions();
    if (!categoryDropdownOpen && !projectDropdownOpen) return;

    window.addEventListener("resize", updatePositions);
    scroller?.addEventListener("scroll", updatePositions);
    return () => {
      window.removeEventListener("resize", updatePositions);
      scroller?.removeEventListener("scroll", updatePositions);
    };
  }, [categoryDropdownOpen, projectDropdownOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(e.target)
      ) {
        setCategoryDropdownOpen(false);
      }
      if (
        projectDropdownRef.current &&
        !projectDropdownRef.current.contains(e.target)
      ) {
        setProjectDropdownOpen(false);
      }
      if (
        savedInsightsDropdownRef.current &&
        !savedInsightsDropdownRef.current.contains(e.target)
      ) {
        setSavedInsightsDropdownOpen(false);
      }
    }
    if (categoryDropdownOpen || projectDropdownOpen || savedInsightsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [categoryDropdownOpen, projectDropdownOpen, savedInsightsDropdownOpen]);

  const projectOptionsForDropdown = useMemo(() => {
    const hasOthers = (ProjectObjs || []).some(
      (p) => (p.name || "").toLowerCase() === "others"
    );
    const projectList = hasOthers
      ? ProjectObjs || []
      : [{ name: "Others" }, ...(ProjectObjs || [])];
    let namesFromProjects = projectList.map((p) => p.name);
    let namesFromTasks = (CurrentObjs || [])
      .map((t) => (t.project || "").trim())
      .filter(Boolean);
    if (categoryFilter.size > 0) {
      const catSet = categoryFilter;
      namesFromProjects = projectList
        .filter((p) => catSet.has((p.category || "General").trim()))
        .map((p) => p.name);
      namesFromTasks = (CurrentObjs || [])
        .filter((t) => catSet.has((t.category || "General").trim()))
        .map((t) => (t.project || "").trim())
        .filter(Boolean);
    }
    return [...new Set([...namesFromProjects, ...namesFromTasks])].sort();
  }, [ProjectObjs, CurrentObjs, categoryFilter]);

  // When category filter changes, remove any selected projects that are no longer in the narrowed list
  useEffect(() => {
    if (projectOptionsForDropdown.length === 0) return;
    setProjectFilter((prev) => {
      if (prev.size === 0) return prev;
      const allowed = new Set(projectOptionsForDropdown);
      const next = new Set([...prev].filter((p) => allowed.has(p)));
      return next.size === prev.size ? prev : next;
    });
  }, [categoryFilter, projectOptionsForDropdown]);

  // Load saved insights from backend (user + account scoped)
  useEffect(() => {
    if (!account_id) {
      setSavedInsights([]);
      return;
    }
    let cancelled = false;
    fetchSavedInsights(account_id, user_id)
      .then((list) => {
        if (!cancelled) setSavedInsights(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setSavedInsights([]);
      });
    return () => { cancelled = true; };
  }, [account_id, user_id]);

  const getCurrentInsightState = () => ({
    searchTerm: searchTerm || "",
    categoryFilter: [...categoryFilter],
    projectFilter: [...projectFilter],
    timePhaseFilter: timePhaseFilter || "",
    insightViewBy: insightViewBy || "project",
    viewType: viewType || "line",
  });

  const saveCurrentInsight = async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed || !account_id) return;
    try {
      const state = getCurrentInsightState();
      const created = await createSavedInsight(account_id, user_id, trimmed, state);
      setSavedInsights((prev) => [created, ...prev]);
      setSaveAsName("");
      setSaveAsOpen(false);
    } catch (err) {
      setError(getMessageForFetchError(err, "Failed to save insight."));
    }
  };

  const applyInsight = (insight) => {
    const s = insight.state || {};
    setSearchTerm(s.searchTerm != null ? s.searchTerm : "");
    setCategoryFilter(new Set(Array.isArray(s.categoryFilter) ? s.categoryFilter : []));
    setProjectFilter(new Set(Array.isArray(s.projectFilter) ? s.projectFilter : []));
    setTimePhaseFilter(s.timePhaseFilter != null ? s.timePhaseFilter : "");
    setInsightViewBy(s.insightViewBy === "task" || s.insightViewBy === "category" ? s.insightViewBy : "project");
    setViewType(["table", "bar", "line", "stepline"].includes(s.viewType) ? s.viewType : "line");
    setCategoryDropdownOpen(false);
    setProjectDropdownOpen(false);
  };

  const deleteInsight = async (id) => {
    if (!account_id) return;
    try {
      await deleteSavedInsight(account_id, user_id, id);
      setSavedInsights((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setError(getMessageForFetchError(err, "Failed to delete insight."));
    }
  };

  // Apply insight from URL when opened from Projects menu (e.g. /FinancialInsights?insightId=xxx)
  const appliedInsightIdRef = useRef(null);
  useEffect(() => {
    const insightId = searchParams.get("insightId");
    if (!insightId || savedInsights.length === 0) return;
    if (appliedInsightIdRef.current === insightId) return;
    const insight = savedInsights.find((i) => i.id === insightId);
    if (insight) {
      appliedInsightIdRef.current = insightId;
      applyInsight(insight);
      setSearchParams({}, { replace: true });
    }
  }, [savedInsights, searchParams, setSearchParams]);

  const { sortedObjs, insightChartData } = useMemo(() => {
    const searchLower = (searchTerm || "").trim().toLowerCase();
    const normalizedStatus = (s) =>
      s && String(s).trim() === "Closed" ? "Closed" : "Open";

    // Time phase filter (matches Dashboard: Upcoming = due within 30 days or missing/invalid; Beyond 30 Days = due after 30-day window only)
    let filteredByTimePhase = CurrentObjs;
    if (timePhaseFilter === "upcoming") {
      const todayVal = dayjs().startOf("day");
      const oneMonthLaterVal = todayVal.add(1, "month").endOf("day");
      filteredByTimePhase = CurrentObjs.filter((obj) => {
        if (normalizedStatus(obj.status) === "Closed") return false;
        const due = getDueInstantForComparison(obj.due_date);
        if (due == null || !due.isValid()) return true;
        const t = due.valueOf();
        return t <= oneMonthLaterVal.valueOf();
      });
    } else if (timePhaseFilter === "remaining") {
      const todayVal = dayjs().startOf("day");
      const oneMonthLaterVal = todayVal.add(1, "month").endOf("day");
      filteredByTimePhase = CurrentObjs.filter((obj) => {
        if (normalizedStatus(obj.status) === "Closed") return false;
        const due = getDueInstantForComparison(obj.due_date);
        if (due == null || !due.isValid()) return false;
        const t = due.valueOf();
        return t > oneMonthLaterVal.valueOf();
      });
    } else if (timePhaseFilter === "closed") {
      // Same as Task/Dashboard: show only tasks with status Closed
      filteredByTimePhase = CurrentObjs.filter(
        (obj) => normalizedStatus(obj.status) === "Closed"
      );
    }

    const filteredByCategory =
      categoryFilter.size === 0
        ? filteredByTimePhase
        : filteredByTimePhase.filter((obj) =>
            categoryFilter.has((obj.category || "General").trim())
          );

    const filteredByProject =
      projectFilter.size === 0
        ? filteredByCategory
        : filteredByCategory.filter((obj) =>
            projectFilter.has((obj.project || "").trim())
          );

    const userByIdForSearch = Object.fromEntries(
      (UserObjs || []).map((u) => [
        u._id,
        (getUserDisplayName(u) || "").toLowerCase(),
      ])
    );

    const filteredObjs = filteredByProject.filter((obj) => {
      if (!searchLower) return true;
      const dueStr = getDueDatePart(obj.due_date);
      const amountStr = obj.amount != null ? String(obj.amount) : "";
      const repeatStr = obj.repeat != null ? String(obj.repeat) : "";
      const ownerDisplay = userByIdForSearch[obj.owner] || "unassigned";
      return (
        (obj.name && obj.name.toLowerCase().includes(searchLower)) ||
        (dueStr && dueStr.toLowerCase().includes(searchLower)) ||
        (amountStr && amountStr.toLowerCase().includes(searchLower)) ||
        (repeatStr && repeatStr.toLowerCase().includes(searchLower)) ||
        (obj.status && obj.status.toLowerCase().includes(searchLower)) ||
        (obj.project && obj.project.toLowerCase().includes(searchLower)) ||
        (obj.category && obj.category.toLowerCase().includes(searchLower)) ||
        (obj.notes && obj.notes.toLowerCase().includes(searchLower)) ||
        (ownerDisplay && ownerDisplay.includes(searchLower))
      );
    });

    const sorted = filteredObjs;
    const insightData = buildTaskInsightChartData(sorted, insightViewBy);
    return { sortedObjs: sorted, insightChartData: insightData };
  }, [
    CurrentObjs,
    UserObjs,
    timePhaseFilter,
    categoryFilter,
    projectFilter,
    searchTerm,
    insightViewBy,
  ]);

  const missingAccountMessage =
    !authLoading && !account_id && (authError || "Missing account information.");
  const displayError = error || missingAccountMessage || "";

  if (authLoading) {
    return (
      <div
        style={{
          minHeight: "calc(100vh - 70px)",
          padding: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Loading…
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "calc(100vh - 70px)",
        padding: "32px 24px 40px",
        background:
          "radial-gradient(circle at top left, #e3f2fd 0, #f3e5f5 35%, #ffffff 75%)",
      }}
    >
      <div
        style={{
          maxWidth: "1120px",
          margin: "0 auto",
        }}
      >
        <div style={{ marginBottom: "24px" }}>
          <div
            style={{
              fontSize: "14px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#6b7280",
              marginBottom: "4px",
            }}
          >
            Financial Insights
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#4b5563",
              marginTop: "4px",
            }}
          >
            Search and filter tasks, then explore financial insights by project,
            task, or category. This view is read-only—use the Task page to edit
            or create tasks.
          </div>
        </div>

        <ErrorBanner
          message={displayError}
          onDismiss={() => setError("")}
        />

        {/* Saved insights dropdown */}
        <div
          ref={savedInsightsDropdownRef}
          style={{
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            position: "relative",
          }}
        >
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setSavedInsightsDropdownOpen(!savedInsightsDropdownOpen)}
              style={{
                padding: "4px 10px",
                fontSize: "13px",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                background: "#fff",
                cursor: "pointer",
                minWidth: "160px",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "6px",
              }}
            >
              <span>
                Saved insights{savedInsights.length > 0 ? ` (${savedInsights.length})` : ""}
              </span>
              <span style={{ fontSize: "10px" }}>
                {savedInsightsDropdownOpen ? "▲" : "▼"}
              </span>
            </button>
            {savedInsightsDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: "2px",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  background: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 100,
                  minWidth: "280px",
                  maxHeight: "280px",
                  overflowY: "auto",
                  padding: "6px 0",
                }}
              >
                {savedInsights.length === 0 ? (
                  <div style={{ padding: "8px 12px", fontSize: "13px", color: "#6c757d" }}>
                    No saved insights. Save current view below.
                  </div>
                ) : (
                  savedInsights.map((insight) => (
                    <div
                      key={insight.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "6px 12px",
                        borderBottom: "1px solid #f1f3f5",
                      }}
                    >
                      <span style={{ flex: "1 1 auto", fontSize: "13px", color: "#212529", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{insight.name}</span>
                      <button
                        type="button"
                        onClick={() => { applyInsight(insight); setSavedInsightsDropdownOpen(false); }}
                        style={{
                          padding: "2px 8px",
                          fontSize: "12px",
                          border: "1px solid #0d6efd",
                          borderRadius: "4px",
                          background: "#0d6efd",
                          color: "#fff",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteInsight(insight.id)}
                        style={{
                          padding: "2px 8px",
                          fontSize: "12px",
                          border: "1px solid #dc3545",
                          borderRadius: "4px",
                          background: "#fff",
                          color: "#dc3545",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {saveAsOpen ? (
              <>
                <input
                  type="text"
                  value={saveAsName}
                  onChange={(e) => setSaveAsName(e.target.value)}
                  placeholder="e.g. Insurance cost trend – last 5 years"
                  style={{
                    width: "280px",
                    maxWidth: "100%",
                    padding: "4px 8px",
                    fontSize: "13px",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveCurrentInsight(saveAsName);
                    if (e.key === "Escape") setSaveAsOpen(false);
                  }}
                />
                <button
                  type="button"
                  onClick={() => saveCurrentInsight(saveAsName)}
                  disabled={!saveAsName.trim()}
                  style={{
                    padding: "4px 12px",
                    fontSize: "12px",
                    border: "1px solid #198754",
                    borderRadius: "4px",
                    background: "#198754",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => { setSaveAsOpen(false); setSaveAsName(""); }}
                  style={{
                    padding: "4px 12px",
                    fontSize: "12px",
                    border: "1px solid #6c757d",
                    borderRadius: "4px",
                    background: "#fff",
                    color: "#6c757d",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setSaveAsOpen(true)}
                style={{
                  padding: "4px 12px",
                  fontSize: "12px",
                  border: "1px solid #0d6efd",
                  borderRadius: "4px",
                  background: "#fff",
                  color: "#0d6efd",
                  cursor: "pointer",
                }}
              >
                Save current view as…
              </button>
            )}
          </div>
        </div>

        {/* Filters row: single row (no horizontal scrollbar) */}
        <div
          ref={filtersRowRef}
          style={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: "6px",
            marginBottom: "12px",
            padding: "6px 0",
            minWidth: 0,
          }}
        >
          <input
            type="text"
            id="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name, due, amount, repeat, status, project, category, notes…"
            style={{
              flex: "1 1 220px",
              minWidth: "140px",
              maxWidth: "220px",
              padding: "4px 8px",
              fontSize: "13px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={() => setSearchTerm("")}
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            Clear
          </button>

          {/* Category filter */}
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#495057",
            }}
          >
            Category:
          </span>
          <div ref={categoryDropdownRef} style={{ position: "relative" }}>
            <button
              ref={categoryButtonRef}
              type="button"
              onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
              style={{
                padding: "4px 10px",
                fontSize: "13px",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                background: "#fff",
                cursor: "pointer",
                minWidth: "120px",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "6px",
              }}
            >
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {categoryFilter.size === 0
                  ? "All"
                  : categoryFilter.size === 1
                    ? [...categoryFilter][0] || "General"
                    : `${categoryFilter.size} selected`}
              </span>
              <span style={{ fontSize: "10px" }}>
                {categoryDropdownOpen ? "▲" : "▼"}
              </span>
            </button>
            {categoryDropdownOpen && (
              <div
                style={{
                  position: "fixed",
                  top: categoryMenuPos?.top ?? 0,
                  left: categoryMenuPos?.left ?? 0,
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  background: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 2000,
                  width: categoryMenuPos?.width ?? 160,
                  maxHeight: "220px",
                  overflowY: "auto",
                  padding: "2px 0",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "4px",
                    padding: "3px 6px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={categoryFilter.size === 0}
                    onChange={() => {
                      setCategoryFilter(new Set());
                    }}
                    style={{ marginTop: "1px", flexShrink: 0 }}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: "left",
                      overflowWrap: "break-word",
                    }}
                  >
                    All
                  </span>
                </label>
                {[...new Set(
                  (CurrentObjs || []).map((t) =>
                    (t.category || "General").trim()
                  )
                )]
                  .sort()
                  .map((cat) => (
                    <label
                      key={cat}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "4px",
                        padding: "3px 6px",
                        fontSize: "13px",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={categoryFilter.has(cat)}
                        onChange={(e) => {
                          const next = new Set(categoryFilter);
                          if (e.target.checked) next.add(cat);
                          else next.delete(cat);
                          setCategoryFilter(next);
                        }}
                        style={{ marginTop: "1px", flexShrink: 0 }}
                      />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          textAlign: "left",
                          overflowWrap: "break-word",
                        }}
                      >
                        {cat || "General"}
                      </span>
                    </label>
                  ))}
              </div>
            )}
          </div>

          {/* Project filter */}
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#495057",
            }}
          >
            Project:
          </span>
          <div ref={projectDropdownRef} style={{ position: "relative" }}>
            <button
              ref={projectButtonRef}
              type="button"
              onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
              style={{
                padding: "4px 10px",
                fontSize: "13px",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                background: "#fff",
                cursor: "pointer",
                minWidth: "120px",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "6px",
              }}
            >
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {projectFilter.size === 0
                  ? "All"
                  : projectFilter.size === 1
                    ? [...projectFilter][0] || "(blank)"
                    : `${projectFilter.size} selected`}
              </span>
              <span style={{ fontSize: "10px" }}>
                {projectDropdownOpen ? "▲" : "▼"}
              </span>
            </button>
            {projectDropdownOpen && (
              <div
                style={{
                  position: "fixed",
                  top: projectMenuPos?.top ?? 0,
                  left: projectMenuPos?.left ?? 0,
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  background: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 2000,
                  width: projectMenuPos?.width ?? 180,
                  maxHeight: "220px",
                  overflowY: "auto",
                  padding: "4px 0",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "4px",
                    padding: "3px 6px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={projectFilter.size === 0}
                    onChange={() => {
                      setProjectFilter(new Set());
                    }}
                    style={{ marginTop: "1px", flexShrink: 0 }}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: "left",
                      overflowWrap: "break-word",
                    }}
                  >
                    All
                  </span>
                </label>
                {projectOptionsForDropdown.map((projName) => (
                  <label
                    key={projName}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "4px",
                      padding: "3px 6px",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={projectFilter.has(projName)}
                      onChange={(e) => {
                        const next = new Set(projectFilter);
                        if (e.target.checked) next.add(projName);
                        else next.delete(projName);
                        setProjectFilter(next);
                      }}
                      style={{ marginTop: "1px", flexShrink: 0 }}
                    />
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: "left",
                        overflowWrap: "break-word",
                      }}
                    >
                      {projName || "(blank)"}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Time filter */}
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#495057",
            }}
          >
            Time:
          </span>
          <select
            value={timePhaseFilter}
            onChange={(e) => setTimePhaseFilter(e.target.value)}
            style={{
              padding: "4px 10px",
              fontSize: "13px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              background: "#fff",
              cursor: "pointer",
              minWidth: "140px",
            }}
          >
            <option value="">All</option>
            <option value="upcoming">Upcoming (30 days)</option>
            <option value="remaining">Beyond 30 Days</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {/* Insights panel (same content as Task insights) */}
        <div
          style={{
            marginTop: "12px",
            padding: "16px",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "nowrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", flexWrap: "nowrap" }}>
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#495057", whiteSpace: "nowrap" }}>View by:</span>
              {["project", "task", "category"].map((opt) => (
                <label key={opt} style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <input type="radio" name="insightViewBy" value={opt} checked={insightViewBy === opt} onChange={() => setInsightViewBy(opt)} />
                  {opt === "project" ? "Project" : opt === "task" ? "Task" : "Category"}
                </label>
              ))}
            </span>
            <span
              style={{
                width: "1px",
                height: "1.2em",
                backgroundColor: "#ced4da",
                flexShrink: 0,
              }}
              aria-hidden
            />
            <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", flexWrap: "nowrap" }}>
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#495057", whiteSpace: "nowrap" }}>View type:</span>
              {[
                { value: "table", label: "Table" },
                { value: "bar", label: "Bar" },
                { value: "line", label: "Line" },
                { value: "stepline", label: "Step" },
              ].map(({ value, label }) => (
                <label key={value} style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <input type="radio" name="viewType" value={value} checked={viewType === value} onChange={() => setViewType(value)} />
                  {label}
                </label>
              ))}
            </span>
          </div>

          <p
            style={{
              margin: "0 0 12px 0",
              fontSize: "12px",
              color: "#6c757d",
            }}
          >
            {sortedObjs.length} task(s)
            {viewType === "table"
              ? " in table view. Read-only."
              : " · X: Month, Y: Amount ($) · " +
                (viewType === "bar"
                  ? "Bar chart shows amount per month."
                  : viewType === "line"
                    ? "Line chart connects values over time."
                    : "Step chart shows changes over time.")}
          </p>

          {viewType === "table" ? (
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                backgroundColor: "#fff",
                overflowX: "auto",
              }}
            >
              <table
                style={{
                  width: "100%",
                  tableLayout: "auto",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600 }}>Name</th>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600 }}>Due Date</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }}>Amount ($)</th>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600 }}>Project</th>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600 }}>Category</th>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600 }}>Status</th>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600 }}>Repeat</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedObjs.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: "16px", color: "#6c757d", textAlign: "center" }}>
                        No tasks match the current filters.
                      </td>
                    </tr>
                  ) : (
                    sortedObjs.map((row) => {
                      const dueStr = getDueDatePart(row.due_date);
                      return (
                        <tr
                          key={row._id}
                          style={{ borderBottom: "1px solid #e9ecef" }}
                        >
                          <td style={{ padding: "8px 10px" }}>{row.name || "—"}</td>
                          <td style={{ padding: "8px 10px" }}>{dueStr || "—"}</td>
                          <td style={{ padding: "8px 10px", textAlign: "right" }}>
                            {row.amount != null ? Number(row.amount).toLocaleString() : "—"}
                          </td>
                          <td style={{ padding: "8px 10px" }}>{row.project || "—"}</td>
                          <td style={{ padding: "8px 10px" }}>{row.category || "General"}</td>
                          <td style={{ padding: "8px 10px" }}>{row.status || "—"}</td>
                          <td style={{ padding: "8px 10px" }}>{row.repeat != null ? row.repeat : "—"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "24px",
              alignItems: "stretch",
              flexWrap: "wrap",
              maxWidth: "100%",
            }}
          >
            <div
              style={{
                flex: "1 1 320px",
                minWidth: 280,
                maxWidth: 480,
                padding: "16px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              <h4
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#212529",
                }}
              >
                About Financial Insights
              </h4>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "18px",
                  fontSize: "13px",
                  lineHeight: 1.6,
                  color: "#495057",
                }}
              >
                <li style={{ marginBottom: 8 }}>
                  <strong>Overview:</strong> Table or charts of task amount ($) over time. Use View by (Project / Task / Category) and View type (Table / Bar / Line / Step).
                </li>
                <li style={{ marginBottom: 8 }}>
                  <strong>Table:</strong> Read-only list: name, due date, amount, project, category, status, repeat.
                </li>
                <li style={{ marginBottom: 8 }}>
                  <strong>View by:</strong> Project = compare across projects; Task = amount per task; Category = breakdown by category. Legend matches colors.
                </li>
                <li style={{ marginBottom: 8 }}>
                  <strong>Sample use cases:</strong> Track insurance or other cost trends by project (View by Project, Line chart); spot high-spend items by project (View by Task, filter by project, Bar or Table); compare spending by category over time (View by Category).
                </li>
                <li style={{ marginBottom: 0 }}>
                  <strong>Charts:</strong> Step = flat until next point; Line = trends; Bar = compare per period.
                </li>
              </ul>
            </div>
            <div style={{ flex: "1 1 480px", minWidth: 320 }}>
              {insightChartData.data.length === 0 ? (
                <p style={{ color: "#6c757d" }}>
                  No data to display for the current filters.
                </p>
              ) : viewType === "bar" ? (
                <TaskBarChart
                  data={insightChartData.data}
                  seriesKeys={insightChartData.seriesKeys}
                  colors={TASK_CHART_COLORS}
                />
              ) : viewType === "line" ? (
                <TaskLineChart
                  data={insightChartData.data}
                  seriesKeys={insightChartData.seriesKeys}
                  colors={TASK_CHART_COLORS}
                />
              ) : (
                <TaskStepLineChart
                  data={insightChartData.data}
                  seriesKeys={insightChartData.seriesKeys}
                  colors={TASK_CHART_COLORS}
                />
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FinancialInsights;

